/**
 * AVMatrix — какие аудиокодеки реально живут в каком видеоконтейнере.
 *
 * Зачем: инструменты давали выбрать контейнер и кодек независимо, и часть
 * комбинаций либо падала в FFmpeg, либо — хуже — собиралась молча и отдавала
 * пользователю файл, который не открывается. /video-audio показывал «Готово!»
 * на Vorbis-в-MP4; /edit-video-sound предлагал WebM+AAC (отказ FFmpeg),
 * WebM+MP3 (отказ), MOV+Opus (отказ) в обычном выпадающем списке.
 *
 * Таблица ниже — НЕ теория. Каждая ячейка получена прогоном настоящего
 * FFmpeg 7.0 на реальном файле (мьюксится или нет), проверкой тега кодека
 * через ffprobe и реальным декодированием в Chromium (не canPlayType —
 * он врёт в обе стороны: заявляет «нет» на mp4/vorbis, который движок
 * на самом деле играет).
 *
 * Три вещи, которые таблица кодирует, и почему их нельзя смешивать:
 *   mux   — FFmpeg вообще может это записать
 *   tag   — контейнер получает ЧЕСТНЫЙ тег кодека. mp3-в-MP4 и vorbis-в-MP4
 *           пишутся с тегом `mp4a`, который означает «здесь AAC». Наш
 *           Chromium такой файл проглотит (у него всеядный демуксер), а
 *           чужой плеер прочитает тег и попробует декодировать MP3 как AAC.
 *           Это и есть «файл скачался, но не открывается».
 *   tier  — насколько широко файл реально откроется у пользователя
 *
 * ⚠ Safari/QuickTime здесь по спецификациям, а НЕ по замеру: WebKit из
 * Playwright на Windows противоречив (canPlayType заявляет `probably` на
 * mp4/h264+aac и при этом не открывает такой файл), то есть как прибор для
 * этого вопроса он невалиден. Не «уточняйте» эти ячейки его прогоном.
 *
 * API:
 *   AVMatrix.check(container, codec)   → {mux, tier, tagOk, note}
 *   AVMatrix.codecsFor(container)      → [{codec, tier, mux, tagOk}]
 *   AVMatrix.recommended(container)    → 'aac' | 'opus' | ...
 *   AVMatrix.coerce(container, codec)  → {codec, changed, from}
 */
(function () {
    'use strict';

    // tier:
    //   universal — откроется практически везде
    //   modern    — современные браузеры и устройства, но НЕ Safari/QuickTime
    //   apple     — родное для экосистемы Apple, мимо остальных
    //   desktop   — VLC и десктопные плееры; не для соцсетей и не для <video>
    //   avoid     — мьюксится, но тег кодека врёт → чужой плеер не откроет
    //   no        — FFmpeg отказывается собирать
    var M = {
        mp4: {
            aac:    { mux: 1, tagOk: 1, tier: 'universal' },
            opus:   { mux: 1, tagOk: 1, tier: 'modern' },   // тег Opus корректен; Safari/QuickTime не умеют
            flac:   { mux: 1, tagOk: 1, tier: 'modern' },   // тег fLaC корректен; без потерь; не для Safari
            alac:   { mux: 1, tagOk: 1, tier: 'apple' },
            ac3:    { mux: 1, tagOk: 1, tier: 'desktop' },  // тег ac-3 корректен; ТВ/ресиверы, не браузер
            mp3:    { mux: 1, tagOk: 0, tier: 'avoid' },    // пишется тегом mp4a = «тут AAC»
            vorbis: { mux: 1, tagOk: 0, tier: 'avoid' }     // пишется тегом mp4a = «тут AAC»
        },
        mov: {
            aac:    { mux: 1, tagOk: 1, tier: 'universal' },
            alac:   { mux: 1, tagOk: 1, tier: 'apple' },
            ac3:    { mux: 1, tagOk: 1, tier: 'desktop' },
            mp3:    { mux: 1, tagOk: 1, tier: 'desktop' },
            vorbis: { mux: 1, tagOk: 0, tier: 'avoid' },    // тег msVo — экзотика
            opus:   { mux: 0, tagOk: 0, tier: 'no' },       // FFmpeg: "opus only supported in MP4"
            flac:   { mux: 0, tagOk: 0, tier: 'no' }
        },
        mkv: {
            aac:    { mux: 1, tagOk: 1, tier: 'desktop' },
            opus:   { mux: 1, tagOk: 1, tier: 'desktop' },
            vorbis: { mux: 1, tagOk: 1, tier: 'desktop' },
            flac:   { mux: 1, tagOk: 1, tier: 'desktop' },
            mp3:    { mux: 1, tagOk: 1, tier: 'desktop' },
            alac:   { mux: 1, tagOk: 1, tier: 'desktop' },
            ac3:    { mux: 1, tagOk: 1, tier: 'desktop' }
        },
        // Спецификация WebM разрешает только Vorbis и Opus — FFmpeg это
        // проверяет и отказывается писать что-либо ещё. Замер подтверждает:
        // всё остальное падает с "Only VP8 or VP9 or AV1 video and Vorbis or
        // Opus audio and WebVTT subtitles are supported for WebM".
        webm: {
            opus:   { mux: 1, tagOk: 1, tier: 'universal' },
            vorbis: { mux: 1, tagOk: 1, tier: 'modern' },
            aac:    { mux: 0, tagOk: 0, tier: 'no' },
            mp3:    { mux: 0, tagOk: 0, tier: 'no' },
            flac:   { mux: 0, tagOk: 0, tier: 'no' },
            alac:   { mux: 0, tagOk: 0, tier: 'no' },
            ac3:    { mux: 0, tagOk: 0, tier: 'no' }
        }
    };

    // Предпочтения по контейнеру — первый пригодный становится рекомендацией
    // и целью авто-коррекции.
    var PREFER = {
        mp4:  ['aac', 'opus', 'flac', 'alac', 'ac3'],
        mov:  ['aac', 'alac', 'ac3', 'mp3'],
        mkv:  ['aac', 'opus', 'flac', 'vorbis', 'mp3'],
        webm: ['opus', 'vorbis']
    };

    // Синонимы: и наши внутренние ключи, и имена энкодеров FFmpeg.
    var ALIAS = {
        libopus: 'opus', libvorbis: 'vorbis', libmp3lame: 'mp3',
        'mp4a': 'aac', 'ac-3': 'ac3', 'mp3float': 'mp3',
        m4a: 'aac', ogg: 'vorbis'
    };

    function normCodec(c) {
        c = String(c || '').toLowerCase().trim();
        return ALIAS[c] || c;
    }
    function normContainer(c) {
        c = String(c || '').toLowerCase().trim().replace(/^\./, '');
        if (c === 'm4v') return 'mp4';
        if (c === 'qt') return 'mov';
        if (c === 'matroska') return 'mkv';
        return c;
    }

    var UNKNOWN = { mux: 1, tagOk: 1, tier: 'desktop', unknown: true };

    window.AVMatrix = {
        /** Сырая таблица — для тестов и отладки. */
        MATRIX: M,

        /**
         * @returns {{mux:boolean, tagOk:boolean, tier:string, playable:boolean,
         *            known:boolean}}
         *   playable — можно отдавать пользователю без оговорок про «не откроется»
         */
        check: function (container, codec) {
            var ct = normContainer(container), cd = normCodec(codec);
            var row = M[ct];
            // Контейнер вне таблицы (пользователь принёс .ts, .flv и т.п.) —
            // не блокируем, у нас нет оснований утверждать, что это не сработает.
            if (!row) { var u = Object.assign({}, UNKNOWN); u.known = false; u.playable = true; return u; }
            var cell = row[cd];
            if (!cell) { var u2 = Object.assign({}, UNKNOWN); u2.known = false; u2.playable = true; return u2; }
            return {
                mux: !!cell.mux,
                tagOk: !!cell.tagOk,
                tier: cell.tier,
                playable: !!cell.mux && !!cell.tagOk && cell.tier !== 'no',
                known: true
            };
        },

        /** Все кодеки контейнера, в порядке предпочтения, с вердиктами. */
        codecsFor: function (container) {
            var ct = normContainer(container), row = M[ct];
            if (!row) return [];
            var order = PREFER[ct] || Object.keys(row);
            var seen = {}, out = [];
            order.concat(Object.keys(row)).forEach(function (cd) {
                if (seen[cd] || !row[cd]) return;
                seen[cd] = 1;
                var c = this.check(ct, cd);
                out.push({ codec: cd, tier: c.tier, mux: c.mux, tagOk: c.tagOk, playable: c.playable });
            }, this);
            return out;
        },

        /** Лучший кодек для контейнера. */
        recommended: function (container) {
            var list = this.codecsFor(container).filter(function (x) { return x.playable; });
            return list.length ? list[0].codec : 'aac';
        },

        /**
         * Привести пару к рабочей. Если выбранный кодек не живёт в этом
         * контейнере — вернуть ближайший пригодный и сказать, что поменяли.
         * Вызывать при КАЖДОЙ смене контейнера, иначе пользователь уносит
         * с собой мёртвую пару.
         */
        coerce: function (container, codec) {
            var cd = normCodec(codec);
            if (cd === 'auto' || cd === 'copy' || cd === 'same') {
                return { codec: cd, changed: false, from: cd };
            }
            var c = this.check(container, cd);
            if (c.playable || !c.known) return { codec: cd, changed: false, from: cd };
            return { codec: this.recommended(container), changed: true, from: cd };
        }
    };
})();
